import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { ParentSize } from '@visx/responsive';
import { scaleLinear, scaleTime } from '@visx/scale';
import { Group } from '@visx/group';
import { GridColumns, GridRows } from '@visx/grid';
import { LinePath, Circle } from '@visx/shape';

import { tickFormatFactory, calculateXAxisTickValues } from '../../utils/chartHelpers';

import PropTypes from 'prop-types';
import { TooltipWithBounds, useTooltip, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import dayjs from 'dayjs';
import { DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE } from '../../utils/dates';
import { gql, useQuery } from '@apollo/client';
import { useAnalysisFilter } from '../../utils/useFeedFrameFilter';

const TOOLTIP_STYLES = {
  ...defaultStyles,
  borderRadius: '8px',
  backgroundColor: 'rgba(40, 40, 40, 0.8)',
  color: 'white',
  border: '1px solid #E6E6E6',
  padding: '1rem',
  minWidth: '50px',
  maxWidth: '300px',
};

function CoefficientChart({
  startDate,
  endDate,
  feed_line_id = null,
  loading,
  // Zoom synchronization props - using clear naming to avoid confusion with startDate/endDate
  sharedZoomDomain = null, // { start: timestamp, end: timestamp }
  onZoomDomainChange = null, // (newDomain) => void
}) {
  const { tooltipData, tooltipLeft = 0, tooltipTop = 0, tooltipOpen, showTooltip, hideTooltip } = useTooltip();

  // Use shared zoom domain if provided, otherwise use internal state
  const [internalStart, setInternalStart] = useState(startDate);
  const [internalEnd, setInternalEnd] = useState(endDate);

  const start = sharedZoomDomain?.start ?? internalStart;
  const end = sharedZoomDomain?.end ?? internalEnd;

  const chartMargin = { top: 10, bottom: 50, left: 30, right: 30 };
  const [zoomStartPoint, setZoomStartPoint] = useState(null);
  const [zoomEndPoint, setZoomEndPoint] = useState(null);

  const FEED_LINE_COEF = gql`
    query FeedLineCoefficientQuery($fl_id: uuid!, $feedLineCoefficientWhere: feed_line_coefficient_bool_exp!) {
      feed_line_coefficient(
        order_by: { started_at: asc }
        where: {
          _and: [{ feed_line_id: { _eq: $fl_id } }, $feedLineCoefficientWhere, { deleted_at: { _is_null: true } }]
        }
      ) {
        started_at
        ended_at
        live_ended_at
        coefficients
      }
    }
  `;
  const feedLineCoefficientWhere = useAnalysisFilter();

  const { data, error } = useQuery(FEED_LINE_COEF, {
    variables: {
      fl_id: feed_line_id,
      feedLineCoefficientWhere,
    },
    skip: !feed_line_id,
  });
  if (error) throw error;

  const feed_line_coefficients = data?.feed_line_coefficient;

  // Calculate dynamic Y-axis domain based on coefficient values
  const yDomain = useMemo(() => {
    const defaultMin = 0.5;
    const defaultMax = 1.5;
    const defaultPadding = 0.1; // 10% padding around default range

    if (!feed_line_coefficients || feed_line_coefficients.length === 0) {
      return [defaultMin, defaultMax]; // Default range when no data
    }

    const coefficientValues = feed_line_coefficients
      .map((co) => co?.coefficients?.linearCorrection)
      .filter((value) => value != null && !isNaN(value));

    if (coefficientValues.length === 0) {
      return [defaultMin, defaultMax]; // Default range when no valid values
    }

    const minValue = Math.min(...coefficientValues);
    const maxValue = Math.max(...coefficientValues);

    // Check if all values fall within the default range plus padding
    const paddedDefaultMin = defaultMin - defaultPadding;
    const paddedDefaultMax = defaultMax + defaultPadding;

    if (minValue >= paddedDefaultMin && maxValue <= paddedDefaultMax) {
      return [defaultMin, defaultMax]; // Use default range if all values fit within padded bounds
    }

    // Add padding above and below the range when expanding beyond defaults
    const padding = Math.max(defaultPadding, (maxValue - minValue) * defaultPadding);
    const paddedMin = Math.max(0, minValue - padding); // Don't go below 0
    const paddedMax = maxValue + padding;

    return [paddedMin, paddedMax];
  }, [feed_line_coefficients]);

  useEffect(() => {
    // Only update internal state if we're not using shared zoom domain
    if (!sharedZoomDomain) {
      setInternalStart(startDate);
      setInternalEnd(endDate);
    }
  }, [startDate, endDate, sharedZoomDomain]);

  return (
    <ParentSize debounceTime={10}>
      {(parent) => {
        const xMax = parent.width - chartMargin.left - chartMargin.right;
        const yMax = parent.height - chartMargin.top - chartMargin.bottom;

        const xAxisTickFormat = tickFormatFactory(start, end);
        const xAxisTicks = calculateXAxisTickValues(start, end);

        const xScale = useCallback(
          scaleTime({
            domain: [start, end],
            range: [0, xMax],
          }),
          [start, end, xMax],
        );
        const yScale = useCallback(
          scaleLinear({
            domain: yDomain,
            range: [yMax, 0],
          }),
          [yMax, yDomain],
        );

        const formatDateTooltip = useCallback((hoveredDate) => {
          return dayjs.unix(hoveredDate).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE);
        }, []);

        const formatDateMMDD = useCallback((timestamp) => {
          return dayjs.unix(timestamp).format('MM/DD');
        }, []);

        const handleTooltip = useCallback(
          (mouseEvent) => {
            // Gets the position of the mouse cursor.
            const point = localPoint(mouseEvent);
            const xCoord = point.x - chartMargin.left;

            // If the mouse pointer is off the chart, we don't want to display any tool tips.
            if (xCoord < 0 || xCoord > xMax) return;
            const xCoordTimestamp = xScale.invert(xCoord).valueOf();
            const mouseTimestamp = xCoordTimestamp; // Keep as milliseconds

            // Find all coefficients that fall within the x value range of the tooltip
            if (feed_line_coefficients && feed_line_coefficients.length > 0) {
              const coefficientsInRange = feed_line_coefficients
                .filter((co) => {
                  // Check if mouse is within the active range of this coefficient
                  const startedAt = co.started_at;
                  const liveEndedAt = co.live_ended_at;

                  // If live_ended_at is null, the coefficient is still active
                  if (liveEndedAt === null) {
                    return mouseTimestamp >= startedAt;
                  }

                  // Otherwise, check if mouse is between started_at and live_ended_at
                  return mouseTimestamp >= startedAt && mouseTimestamp <= liveEndedAt;
                })
                .toSorted((a, b) => b.started_at - a.started_at);

              if (coefficientsInRange.length > 0) {
                // Use the first coefficient for positioning
                const referenceCoef = coefficientsInRange[0];
                showTooltip({
                  tooltipLeft: xCoord,
                  tooltipTop: yScale(referenceCoef.coefficients.linearCorrection),
                  tooltipData: {
                    coefficients: coefficientsInRange.map((co) => ({
                      value: co.coefficients.linearCorrection,
                      started_at: co.started_at,
                      ended_at: co.ended_at,
                      live_ended_at: co.live_ended_at,
                      started_at_formatted: co.started_at ? formatDateMMDD(co.started_at) : null,
                      ended_at_formatted: co.ended_at ? formatDateMMDD(co.ended_at) : null,
                      live_ended_at_formatted: co.live_ended_at ? formatDateMMDD(co.live_ended_at) : null,
                    })),
                    displayedDate: formatDateTooltip(mouseTimestamp),
                  },
                });
              } else {
                // Hide tooltip if no coefficients found
                hideTooltip();
              }
            }
          },
          [feed_line_coefficients, xMax, xScale, yScale, formatDateTooltip, formatDateMMDD, showTooltip, hideTooltip],
        );

        const linePaths = useMemo(() => {
          if (!feed_line_coefficients) return [];

          return feed_line_coefficients.map((co, index) => {
            // Create three points for each coefficient: started_at, ended_at, live_ended_at
            const timePoints = [
              co.started_at,
              co.ended_at || co.started_at, // fallback to started_at if ended_at is null
              co.live_ended_at || co.ended_at || co.started_at, // fallback chain
            ].filter(Boolean); // Remove any null/undefined values

            const yValue = co?.coefficients?.linearCorrection;

            return (
              <React.Fragment key={`coefficient-line-${co.started_at}-${index}`}>
                <LinePath
                  data={timePoints}
                  x={(value) => xScale(value)}
                  y={() => yScale(yValue)} // All points have the same Y value
                  stroke={'#000000'}
                  strokeWidth={2}
                  strokeOpacity={1}
                  className={'ChartLinePath'}
                />
                {co.live_ended_at == null && co.ended_at != null && (
                  <LinePath
                    data={[co.ended_at, end]}
                    x={(value) => xScale(value)}
                    y={() => yScale(yValue)} // All points have the same Y value
                    stroke={'#000000'}
                    strokeWidth={2}
                    strokeDasharray={[1, 5]}
                    strokeOpacity={1}
                    className={'ChartLinePath-dashed'}
                  />
                )}
              </React.Fragment>
            );
          });
        }, [xScale, yScale, feed_line_coefficients]);

        const markers = useMemo(() => {
          if (!feed_line_coefficients) return [];

          return feed_line_coefficients.flatMap((co, coIndex) => {
            const yValue = co?.coefficients?.linearCorrection;

            // Create markers for all three time points
            const timePoints = [
              { time: co.started_at, type: 'started_at' },
              { time: co.ended_at, type: 'ended_at' },
              { time: co.live_ended_at, type: 'live_ended_at' },
            ].filter((point) => point.time != null); // Only include points with valid timestamps

            return timePoints.map((point) => (
              <circle
                r={3}
                key={`${coIndex}-${point.type}-${point.time}`}
                cx={xScale(point.time)}
                cy={yScale(yValue)}
                fill={point.type === 'started_at' ? '#4CAF50' : point.type === 'ended_at' ? '#FF9800' : '#2196F3'}
                strokeWidth={0}
              />
            ));
          });
        }, [xScale, yScale, feed_line_coefficients]);

        const svgCursor = loading ? 'wait' : zoomStartPoint && zoomEndPoint ? 'zoom-in' : 'crosshair';

        return (
          <div className="EventChart" key={'EventChart'}>
            <svg
              className="EventChart-chartSvg"
              style={{
                height: parent.height,
                width: parent.width,
                userSelect: 'none',
                cursor: svgCursor,
              }}
              onTouchStart={handleTooltip}
              onTouchMove={handleTooltip}
              onMouseMove={(event) => {
                handleTooltip(event);
                if (zoomStartPoint) {
                  setZoomEndPoint(localPoint(event).x);
                }
              }}
              onMouseLeave={hideTooltip}
              onMouseDown={(event) => {
                setZoomStartPoint(localPoint(event).x);
              }}
              onMouseUp={() => {
                const startPoint = zoomStartPoint > zoomEndPoint ? zoomEndPoint : zoomStartPoint;
                const endPoint = zoomStartPoint <= zoomEndPoint ? zoomEndPoint : zoomStartPoint;

                if (startPoint && endPoint && 20 < endPoint - startPoint) {
                  const newStart = xScale.invert(startPoint - chartMargin.left);
                  const newEnd = xScale.invert(endPoint - chartMargin.left);

                  if (onZoomDomainChange) {
                    onZoomDomainChange({ start: newStart, end: newEnd });
                  } else {
                    setInternalStart(newStart);
                    setInternalEnd(newEnd);
                  }
                }

                setZoomStartPoint(null);
                setZoomEndPoint(null);
              }}
              onDoubleClick={() => {
                if (onZoomDomainChange) {
                  onZoomDomainChange({ start: startDate, end: endDate });
                } else {
                  setInternalStart(startDate);
                  setInternalEnd(endDate);
                }
              }}
            >
              <Group
                left={chartMargin.left}
                top={chartMargin.top}
                bottom={chartMargin.bottom}
                right={chartMargin.right}
              >
                <defs>
                  <clipPath id="eventInnerChartHorizontalBoundary">
                    <rect x={0} y={0} width={xMax} height={yMax} />
                  </clipPath>
                </defs>
                <GridColumns
                  scale={xScale}
                  width={xMax}
                  height={yMax}
                  stroke="#D9DCE1"
                  strokeWidth={2}
                  tickValues={xAxisTicks}
                />

                <AxisBottom
                  top={yMax}
                  scale={xScale}
                  hideTicks
                  stroke="#D9DCE1"
                  tickFormat={xAxisTickFormat}
                  tickValues={xAxisTicks}
                />

                <GridRows scale={yScale} width={xMax} height={yMax} stroke="#D9DCE1" strokeWidth={2} numTicks={6} />

                <AxisLeft top={0} scale={yScale} hideTicks stroke="#D9DCE1" numTicks={6} />

                {zoomStartPoint && zoomEndPoint && (
                  <rect
                    height={yMax}
                    y={0}
                    x={(zoomStartPoint < zoomEndPoint ? zoomStartPoint : zoomEndPoint) - chartMargin.left}
                    width={Math.abs(zoomEndPoint - zoomStartPoint)}
                    fill={`#00000022`}
                  ></rect>
                )}

                {
                  // Create the vertical dashed line and circular intercept markers that accompany the tooltip.
                  // Do not show if someone is interacting with the projection brushes.
                  tooltipOpen && (
                    <g pointerEvents="none">
                      <line
                        stroke="#000"
                        strokeWidth="1"
                        x1={tooltipLeft}
                        x2={tooltipLeft}
                        y1={0}
                        y2={yMax}
                        strokeDasharray={(5, 5)}
                      />
                      <Circle
                        key={0}
                        className="EventChart-chartInterceptCircleSvg"
                        cx={tooltipLeft}
                        cy={yScale(tooltipData.coefficients[0]?.value || 0)}
                        r={10}
                        stroke={'grey'}
                        fill={'white'}
                      />
                    </g>
                  )
                }
                {linePaths}
                {markers}
              </Group>
            </svg>
            {
              // Create the tooltip unless someone is interacting with the projection brushes.
              tooltipOpen && (
                <TooltipWithBounds top={tooltipTop - 50} left={tooltipLeft + 35} style={TOOLTIP_STYLES}>
                  <div
                    className={`EventChart-tooltipInfo`}
                    style={{ minWidth: '50px', maxWidth: '300px', width: 'auto' }}
                  >
                    {
                      <>
                        <p className="EventChart-tooltipInfoHeader">{tooltipData.displayedDate}</p>
                        <div
                          className="EventChart-tooltipInfo-dataholder"
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          {tooltipData.coefficients.map((coef) => (
                            <div
                              key={coef.started_at}
                              style={{
                                marginBottom: '8px',
                                borderBottom:
                                  coef.started_at !== tooltipData.coefficients.at(-1)?.started_at
                                    ? '1px solid #444'
                                    : 'none',
                                paddingBottom: '8px',
                                display: 'block',
                              }}
                            >
                              <div className="EventChart-tooltipInfo-dataholder-mass">
                                <div className={`EventChart-tooltipCircle`} style={{ backgroundColor: 'grey' }} />
                                <span className="EventChart-tooltipInfo-dataholder-text">{coef.value}</span>
                              </div>
                              <div
                                style={{
                                  marginTop: '4px',
                                  fontSize: '11px',
                                  color: 'white',
                                  wordBreak: 'break-word',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '200px',
                                }}
                              >
                                {coef.started_at_formatted ?? '??'} - {coef.ended_at_formatted ?? '??'} -{' '}
                                {coef.live_ended_at_formatted ?? '??'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    }
                  </div>
                </TooltipWithBounds>
              )
            }
          </div>
        );
      }}
    </ParentSize>
  );
}

CoefficientChart.propTypes = {
  startDate: PropTypes.number.isRequired,
  endDate: PropTypes.number.isRequired,
  feed_line_id: PropTypes.string,
  loading: PropTypes.bool,
  sharedZoomDomain: PropTypes.shape({
    start: PropTypes.number,
    end: PropTypes.number,
  }),
  onZoomDomainChange: PropTypes.func,
};

export default CoefficientChart;
